/**
 * Vivah Sansar Sarees — Luxury JS
 * Scroll reveals, gold shipping bar, gift wrap, mobile menu polish
 */

(function () {
  'use strict';

  /* ─── Intersection Observer Reveal ─────────────────────── */
  const REVEAL_SEL = '[data-vs-reveal], [data-vs-stagger]';

  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  function initReveals() {
    document.querySelectorAll(REVEAL_SEL).forEach((el) => {
      revealObserver.observe(el);
    });
  }

  /* ─── Free Shipping Bar ─────────────────────────────────── */
  function updateShippingBar() {
    const bars = document.querySelectorAll('[data-vs-shipping-bar]');
    if (!bars.length) return;

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const total     = cart.total_price; // in paise/cents
        const threshold = 100000;           // ₹1000 in paise — configurable
        const pct       = Math.min((total / threshold) * 100, 100);
        const remaining = Math.max((threshold - total) / 100, 0);

        bars.forEach((bar) => {
          const fill = bar.querySelector('[data-vs-shipping-fill]');
          const text = bar.querySelector('[data-vs-shipping-text]');
          if (fill) fill.style.width = pct + '%';
          if (text) {
            if (pct >= 100) {
              text.innerHTML = '🎉 You qualify for <strong>Free Shipping!</strong>';
            } else {
              text.innerHTML =
                'Add <strong>₹' + remaining.toFixed(0) + '</strong> more for Free Shipping';
            }
          }
        });
      })
      .catch(() => {});
  }

  /* ─── Gift Wrap Toggle ──────────────────────────────────── */
  function initGiftWrap() {
    document.querySelectorAll('[data-vs-gift-wrap]').forEach((wrap) => {
      const checkbox = wrap.querySelector('[data-vs-gift-check]');
      if (!checkbox) return;

      wrap.addEventListener('click', () => {
        const isChecked = checkbox.classList.toggle('is-checked');
        // Add/remove gift wrap line item attribute if needed
        wrap.setAttribute('aria-pressed', isChecked);
      });
    });
  }

  /* ─── Wishlist (localStorage) ──────────────────────────── */
  function initWishlist() {
    const KEY = 'vs_wishlist_items';
    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (_) {}

    function save() {
      try {
        localStorage.setItem(KEY, JSON.stringify(items));
      } catch (_) {}
      updateBadgesAndButtons();
      document.dispatchEvent(new CustomEvent('vs:wishlist:updated', { detail: { items } }));
    }

    function updateBadgesAndButtons() {
      // Update buttons active class
      document.querySelectorAll('[data-vs-wishlist]').forEach((btn) => {
        const id = btn.dataset.vsWishlist;
        const exists = items.some((item) => String(item.id) === String(id));
        if (exists) {
          btn.classList.add('active', 'is-active');
          btn.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          btn.classList.remove('active', 'is-active');
          btn.setAttribute('aria-label', 'Add to wishlist');
        }
      });

      // Update counters & bubbles
      document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
        badge.textContent = items.length;
      });

      document.querySelectorAll('[data-wishlist-bubble]').forEach((bubble) => {
        const btn = bubble.closest('.header-actions__wishlist-icon');
        if (items.length > 0) {
          bubble.style.display = '';
          if (btn) btn.classList.add('header-actions__cart-icon--has-cart');
        } else {
          bubble.style.display = 'none';
          if (btn) btn.classList.remove('header-actions__cart-icon--has-cart');
        }
      });

      document.querySelectorAll('.vs-mobile-bottom-nav__badge[data-wishlist-count]').forEach((badge) => {
        badge.style.display = items.length > 0 ? 'inline-flex' : 'none';
      });
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-vs-wishlist]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const id = btn.dataset.vsWishlist;
      const idx = items.findIndex((item) => String(item.id) === String(id));

      if (idx === -1) {
        items.push({
          id: id,
          handle: btn.dataset.productHandle || '',
          title: btn.dataset.productTitle || '',
          price: btn.dataset.productPrice || '',
          img: btn.dataset.productImg || '',
          url: btn.dataset.productUrl || '#'
        });
      } else {
        items.splice(idx, 1);
      }

      save();

      // Heart pop animation
      if (btn.animate) {
        btn.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }],
          { duration: 280, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
        );
      }
    });

    // Initial sync
    updateBadgesAndButtons();
  }

  /* ─── Announcement Bar Marquee ──────────────────────────── */
  function initMarquee() {
    document.querySelectorAll('[data-vs-marquee]').forEach((track) => {
      // Clone content for infinite scroll
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentNode.appendChild(clone);
    });
  }

  /* ─── Sticky Header Shrink ──────────────────────────────── */
  function initStickyHeader() {
    const header = document.querySelector('#header-component');
    if (!header) return;
    // Class toggled by native Shopify header.js; we just add visual polish
    const obs = new IntersectionObserver(
      ([entry]) => {
        header.setAttribute('data-scrolled', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-1px 0px 0px 0px' }
    );
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;';
    document.body.prepend(sentinel);
    obs.observe(sentinel);
  }

  /* ─── Image Lazy Fade ───────────────────────────────────── */
  function initLazyFade() {
    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      if (img.complete) return;
      img.style.opacity = '0';
      img.addEventListener('load', () => {
        img.style.transition = 'opacity 0.4s ease';
        img.style.opacity = '1';
      }, { once: true });
    });
  }

  /* ─── Accordion (FAQ sections) ──────────────────────────── */
  function initFaqAccordions() {
    document.querySelectorAll('[data-vs-accordion]').forEach((acc) => {
      const triggers = acc.querySelectorAll('[data-vs-accordion-trigger]');
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const panel = document.getElementById(trigger.getAttribute('aria-controls'));
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Close all
          triggers.forEach((t) => {
            t.setAttribute('aria-expanded', 'false');
            const p = document.getElementById(t.getAttribute('aria-controls'));
            if (p) { p.hidden = true; }
          });

          if (!isOpen && panel) {
            trigger.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
          }
        });
      });
    });
  }

  /* ─── Testimonial Auto-Rotate ───────────────────────────── */
  function initTestimonialSlider() {
    document.querySelectorAll('[data-vs-testimonials]').forEach((slider) => {
      const items  = slider.querySelectorAll('[data-vs-testimonial-item]');
      const dots   = slider.querySelectorAll('[data-vs-testimonial-dot]');
      if (items.length < 2) return;

      let current = 0;
      let timer;

      function goTo(idx) {
        items[current].classList.remove('is-active');
        if (dots[current]) dots[current].classList.remove('is-active');
        current = (idx + items.length) % items.length;
        items[current].classList.add('is-active');
        if (dots[current]) dots[current].classList.add('is-active');
      }

      function autoPlay() {
        timer = setInterval(() => goTo(current + 1), 4500);
      }

      goTo(0);
      autoPlay();

      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          clearInterval(timer);
          goTo(i);
          autoPlay();
        });
      });

      // Pause on hover
      slider.addEventListener('mouseenter', () => clearInterval(timer));
      slider.addEventListener('mouseleave', autoPlay);
    });
  }

  /* ─── Quick Add To Cart Flow ────────────────────────────── */
  window.vsAddCartItem = async function (variantId, btn) {
    if (!variantId || btn.dataset.loading === 'true') return;
    btn.dataset.loading = 'true';
    const orig = btn.innerHTML;

    btn.innerHTML = `
      <svg class="vs-spinner" viewBox="0 0 50 50" width="16" height="16" style="animation: vs-spin 1s linear infinite; display: inline-block; vertical-align: middle;">
        <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" style="stroke-dasharray: 80, 200; stroke-dashoffset: 0;"></circle>
      </svg>
      <style>
        @keyframes vs-spin {
          100% { transform: rotate(360deg); }
        }
      </style>
    `;

    // Temporarily disable auto-open on the cart drawer so it doesn't slide open
    const drawerComponent = document.querySelector('cart-drawer-component');
    const hasAutoOpen = drawerComponent ? drawerComponent.hasAttribute('auto-open') : false;
    if (drawerComponent && hasAutoOpen) {
      drawerComponent.removeAttribute('auto-open');
    }

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      // Restore auto-open attribute after event promise resolves or rejects
      deferredEventPromise.promise.finally(() => {
        if (drawerComponent && hasAutoOpen) {
          drawerComponent.setAttribute('auto-open', '');
        }
      });

      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [
            {
              merchandiseId: String(variantId),
              quantity: 1,
            },
          ],
          promise: deferredEventPromise.promise,
        })
      );

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });
      if (sectionIds.indexOf('cart-drawer-section') === -1) {
        sectionIds.push('cart-drawer-section');
      }

      const body = JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      const result = await response.json();

      if (result.status) {
        throw new Error(result.message || 'Add to cart failed');
      }

      const cartResponse = await fetch('/cart.js');
      const cartJson = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
        detail: {
          items: cartJson.items,
          source: 'vs-product-card-item',
          sourceId: 'vs-quick-bag-btn',
          itemCount: cartJson.item_count,
          productId: result.product_id,
          sections: result.sections,
          didError: false,
        },
      });

      // Show success checkmark
      btn.innerHTML = `
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `;
      btn.style.background = '#C29A5B';
      btn.style.color = '#FFFFFF';

      document.dispatchEvent(new CustomEvent('cart:updated'));

      setTimeout(() => {
        btn.innerHTML = orig;
        btn.style.background = '';
        btn.style.color = '';
        btn.dataset.loading = 'false';
      }, 1200);

    } catch (err) {
      console.error('[vs-quick-add] Error adding to cart:', err);
      if (drawerComponent && hasAutoOpen) {
        drawerComponent.setAttribute('auto-open', '');
      }
      btn.innerHTML = `
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      `;
      btn.style.background = '#FF5252';
      btn.style.color = '#FFFFFF';
      setTimeout(() => {
        btn.innerHTML = orig;
        btn.style.background = '';
        btn.style.color = '';
        btn.dataset.loading = 'false';
      }, 1500);
    }
  };

  /* ─── Account Popover Logout Button Injection ────────────── */
  function initAccountLogoutButton() {
    function isUserLoggedIn(root) {
      // 1. Check data attribute on body
      if (document.body.dataset.customerLoggedIn === 'true') {
        return true;
      }
      if (document.body.dataset.customerLoggedIn === 'false') {
        return false;
      }

      // 2. Fallback DOM inspection: if container contains "Sign in" or "Continue with Google", user is NOT logged in!
      const rootText = (root ? (root.textContent || '') : '');
      if (rootText.includes('Sign in') || rootText.includes('create account') || rootText.includes('Continue with Google') || rootText.includes('signed-out-avatar')) {
        return false;
      }

      return false;
    }

    function injectLogoutInto(root) {
      if (!root) return;

      // Ensure user is logged in before showing logout button
      if (!isUserLoggedIn(root)) {
        // Remove any injected logout buttons if user is signed out
        root.querySelectorAll('.vs-logout-wrapper, .vs-logout-btn').forEach((btn) => btn.remove());
        return;
      }

      // Look for popover modal container or menu list inside root
      const navList = root.querySelector('nav, ul, ol, [part="menu"], [class*="menu"], [class*="content"], [class*="dialog"], [class*="popover"], .account-menu');
      const targetContainer = navList || root;

      // Don't inject twice
      if (targetContainer.querySelector('a[href*="/account/logout"]') || targetContainer.querySelector('.vs-logout-btn')) {
        return;
      }

      // Check if user menu items exist (e.g. Profile, Orders, Contact Us, About Us)
      const existingLinks = Array.from(targetContainer.querySelectorAll('a'));
      if (!existingLinks.length && !targetContainer.children.length) return;

      const isList = targetContainer.tagName && (targetContainer.tagName.toLowerCase() === 'ul' || targetContainer.tagName.toLowerCase() === 'ol');

      const logoutBtn = document.createElement(isList ? 'li' : 'div');
      logoutBtn.className = 'vs-logout-wrapper';
      logoutBtn.style.cssText = 'border-top: 1px solid rgba(0,0,0,0.08); margin-top: 8px; padding-top: 4px; list-style: none;';

      logoutBtn.innerHTML = `
        <a href="/account/logout" class="vs-logout-btn" style="
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          color: #d32f2f !important;
          font-weight: 600;
          font-size: 0.9375rem;
          font-family: inherit;
          text-decoration: none !important;
          cursor: pointer;
          border-radius: 6px;
          transition: background-color 0.2s ease;
        " onmouseover="this.style.backgroundColor='#fff0f0'" onmouseout="this.style.backgroundColor='transparent'">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Logout
        </a>
      `;

      targetContainer.appendChild(logoutBtn);
    }

    function checkAndInject() {
      // 1. Check shopify-account elements - only inject into the deepest available container
      document.querySelectorAll('shopify-account').forEach((accountEl) => {
        if (accountEl.shadowRoot) {
          // Prefer the dialog inside shadow root if present
          const dialog = accountEl.shadowRoot.querySelector('dialog, [part="dialog"], [role="dialog"], [class*="dialog"], [class*="popover"]');
          if (dialog) {
            injectLogoutInto(dialog);
          } else {
            injectLogoutInto(accountEl.shadowRoot);
          }
        } else {
          injectLogoutInto(accountEl);
        }
      });
    }

    checkAndInject();
    setTimeout(checkAndInject, 300);
    setTimeout(checkAndInject, 1000);

    // Re-check on click of account buttons / triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('shopify-account, .account-button, [class*="account"]')) {
        setTimeout(checkAndInject, 50);
        setTimeout(checkAndInject, 150);
        setTimeout(checkAndInject, 400);
      }
    });

    // Observe shopify-account mutations
    document.querySelectorAll('shopify-account').forEach((accountEl) => {
      const observer = new MutationObserver(checkAndInject);
      observer.observe(accountEl, { childList: true, subtree: true });
      if (accountEl.shadowRoot) {
        const shadowObserver = new MutationObserver(checkAndInject);
        shadowObserver.observe(accountEl.shadowRoot, { childList: true, subtree: true });
      }
    });
  }

  /* ─── AirReviews Modal Fix (JS fallback) ────────────────── */
  function initReviewModalFix() {
    var mobileNav = document.querySelector('.vs-mobile-bottom-nav');
    if (!mobileNav) return;

    // Toggle mobile nav visibility when review modal opens/closes
    function toggleNav() {
      var modal = document.querySelector('[class*="AirReviews-Modal__Overlay"], [class*="AirReviews-Modal"][style*="display: flex"], [class*="AirReviews-Modal"][style*="display:flex"]');
      if (modal) {
        mobileNav.style.display = 'none';
        document.body.style.paddingBottom = '0';
      } else {
        mobileNav.style.display = '';
        document.body.style.paddingBottom = '';
      }
    }

    // Observe DOM for modal opening/closing
    var observer = new MutationObserver(function() {
      toggleNav();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  }

  /* ─── DOM-ready init ────────────────────────────────────── */
  function init() {
    initReveals();
    initWishlist();
    initGiftWrap();
    initMarquee();
    initStickyHeader();
    initLazyFade();
    initFaqAccordions();
    initTestimonialSlider();
    updateShippingBar();
    initAccountLogoutButton();
    initReviewModalFix();

    // Re-init reveals after Shopify section re-renders
    document.addEventListener('shopify:section:load', () => {
      initReveals();
      initTestimonialSlider();
      initFaqAccordions();
      updateShippingBar();
      initAccountLogoutButton();
    });

    // Update shipping bar after cart changes
    document.addEventListener('cart:updated', updateShippingBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
